import CheckmarkIcon from '@assets/icons/checkmark.svg';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { AdCampaignEventType, SampleRequestStatus } from '@/enums';
import { AdCampaignModuleType } from '@/types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { formatDate } from '@/utils/date';
import {
  getNextAvailableSampleRequestDate,
  isSampleRequestLimitReached,
  trackAdAnalytics,
} from '@/utils/ads';
import { ItemListOption } from '@/contexts/item-context-types';
import { MultiStyleText } from '@/components/multi-style-text';
import { SampleRequestConfirmationActionSheet } from './sample-request-confirmation-action-sheet';
import { SampleRequestLimitMessage } from './sample-request-limit-message';
import { StyleProp, Text, TouchableOpacity, ViewStyle } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useAuthenticatedUserContext } from '@/contexts/user-context';

interface Props {
  buttonText?: string;
  option: ItemListOption;
  source: string;
  style?: StyleProp<ViewStyle>;
}

export const LIST_VIEW_BUTTON_WIDTH = 260;

export const SampleRequest: React.FC<Props> = ({
  buttonText,
  option,
  source,
  style,
}) => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID, timezone },
    user,
  } = useAuthenticatedUserContext();

  const [styles, theme] = useStyles();

  const [showSampleConfirmation, setShowSampleConfirmation] = useState(false);
  const [showLimitReachedMessage, setShowLimitReachedMessage] = useState(false);
  const [showPreviousRequestMessage, setShowPreviousRequestMessage] =
    useState(false);

  const sampleRequestSku = useMemo(
    () => option.metadata?.skus.find((sku) => sku.sampleRequest),
    [option],
  );
  const sampleRequest = sampleRequestSku?.sampleRequest;
  const previousRequest = sampleRequest?.sample_request;
  const previousRequestStatus = previousRequest?.status as SampleRequestStatus;

  const getSampleRequestButtonLabel = (status: SampleRequestStatus) => {
    if (status === SampleRequestStatus.APPROVED) {
      return 'sample_approved.button';
    }
    return 'sample_requested.button';
  };

  const getSampleRequestTitle = (status: SampleRequestStatus) => {
    if (status === SampleRequestStatus.APPROVED) {
      return 'sample_approved.title';
    }
    return 'sample_requested.title';
  };

  const getSampleRequestMessage = (status: SampleRequestStatus) => {
    if (status === SampleRequestStatus.APPROVED) {
      return 'sample_approved.message';
    }
    return 'sample_in_review.message';
  };

  const requestLimitReached = isSampleRequestLimitReached(sampleRequest);

  return sampleRequestSku && sampleRequest ? (
    // Use TouchableOpacity to prevent selectItemListOption propagation
    <TouchableOpacity activeOpacity={1} focusable={false}>
      <Button
        containerStyle={style}
        focusable={false}
        leftElement={
          previousRequest &&
          previousRequest.status === SampleRequestStatus.APPROVED ? (
            <CheckmarkIcon
              height={20}
              stroke={theme.colors.primary}
              testID="icon-sample-request-approved"
              width={20}
            />
          ) : null
        }
        onPress={() => {
          if (requestLimitReached) {
            setShowLimitReachedMessage(true);
          } else if (previousRequest) {
            setShowPreviousRequestMessage(true);
          } else {
            trackAdAnalytics({
              adCampaignUUID: sampleRequest.ad_campaign_id,
              adManagerUUID: sampleRequest.ad_manager_id,
              adModule: AdCampaignModuleType.SampleRequest,
              advertiserUUID: sampleRequest.advertiser_id,
              distributorUUID: supplierUUID,
              eventType: AdCampaignEventType.CLICK,
              eventUUID: sampleRequest.sample_request_event_data.click_event_id,
              gtin: sampleRequest.gtin,
              operatorUUID: restaurantUUID,
              source,
              userUUID: user.uuid,
              variantPackUUID: sampleRequestSku.variantPackUUID,
            });
            setShowSampleConfirmation(true);
          }
        }}
        size="small"
        style={[
          styles.baseRequestButton,
          (requestLimitReached && !previousRequest) || previousRequestStatus
            ? styles.limitReached
            : styles.button,
        ]}
        textStyle={requestLimitReached ? styles.limitReachedText : null}
        trackingData={{
          name: 'sample_request.button',
          properties: { variantPackUUID: sampleRequestSku.variantPackUUID },
        }}
        type="secondary"
      >
        {i18n.t(
          previousRequest
            ? getSampleRequestButtonLabel(previousRequestStatus)
            : (buttonText ?? 'sample_request.button'),
        )}
      </Button>
      {showSampleConfirmation ? (
        <SampleRequestConfirmationActionSheet
          onClose={() => setShowSampleConfirmation(false)}
          sampleRequestSku={sampleRequestSku}
        />
      ) : null}
      {showLimitReachedMessage ? (
        <BaseActionSheet
          isVisible
          onClose={() => setShowLimitReachedMessage(false)}
        >
          <Text style={[TYPOGRAPHY.h2, styles.sampleRequested]}>
            {i18n.t('sample_request.request_more_on', {
              date: getNextAvailableSampleRequestDate(
                sampleRequest.earliest_requested_at,
                sampleRequest.lookback_period_days,
                timezone,
              ),
            })}
          </Text>
          <SampleRequestLimitMessage sampleRequest={sampleRequest} />
        </BaseActionSheet>
      ) : null}
      {showPreviousRequestMessage ? (
        <BaseActionSheet
          isVisible
          onClose={() => setShowPreviousRequestMessage(false)}
        >
          <Text style={[TYPOGRAPHY.h2, styles.sampleRequested]}>
            {i18n.t(getSampleRequestTitle(previousRequestStatus), {
              name: sampleRequestSku.displayName,
            })}
          </Text>
          {previousRequestStatus ? (
            <Text style={[TYPOGRAPHY.p, styles.sampleRequested]}>
              {i18n.t(getSampleRequestMessage(previousRequestStatus))}
            </Text>
          ) : null}
          <MultiStyleText
            defaultStyle={[TYPOGRAPHY.p, styles.sampleRequested]}
            emphasisStyle={TYPOGRAPHY.pSemiBold}
          >
            {i18n.t('sample_requested.message', {
              date: formatDate(previousRequest!.requested_at),
              requestor:
                previousRequest!.requested_by?.supplier_employee_overrides[0]
                  ?.name ??
                previousRequest!.requested_by?.name ??
                '',
            })}
          </MultiStyleText>
          <SampleRequestLimitMessage sampleRequest={sampleRequest} />
        </BaseActionSheet>
      ) : null}
    </TouchableOpacity>
  ) : null;
};

const useStyles = createStyles((theme) => ({
  baseRequestButton: {
    height: 'auto',
    minHeight: 36,
  },
  button: {
    borderColor: theme.colors.accentHeavy,
  },
  limitReached: {
    borderColor: theme.colors.divider,
  },
  limitReachedText: {
    color: theme.colors.inactiveText,
  },
  sampleRequested: {
    marginBottom: 16,
  },
}));
